import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageContainerAB from '@/components/PageContainerAB/pageContainer';
import { Icons } from '@/assets';
import { Icon } from '@/components/Icon';
import helpCenterBackground from '@/assets/img/video-help-center.svg';
import IconButton from '@/ctComponents/IconButton';
import { usePermissions } from 'react-admin';
import {
  ContactSupport,
  VideoCard,
  UploadVideo,
  ViewersList,
} from './components';
import Modal from '@/ctComponents/Modal';
import { createPortal } from 'react-dom';
import {
  deleteHelpCenterVideo,
  getAdminHelpCenterVideos,
  getUserHelpCenterVideos,
  SaveApiResMessage,
} from '@/actions';
import LoadingV2 from '@/components/Loading';

const HelpCenter = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadVideo, setShowUploadVideo] = useState({
    open: false,
    data: null,
  });
  const [showDeleteVideo, setShowDeleteVideo] = useState({
    open: false,
    data: null,
  });
  const [showViewersList, setShowViewersList] = useState({
    open: false,
    data: null,
  });
  const [helpTopics, setHelpTopics] = useState([]);

  const { permissions } = usePermissions();

  const isAdmin = useMemo(() => permissions === 'admin', [permissions]);
  const isUser = useMemo(() => permissions === 'user', [permissions]);

  const fetchList = useCallback(() => {
    if (isAdmin) {
      setIsLoading(true);
      getAdminHelpCenterVideos(searchQuery)
        .then((res) => {
          setHelpTopics(res.data.videos);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else if (isUser) {
      setIsLoading(true);
      getUserHelpCenterVideos(searchQuery)
        .then((res) => {
          setHelpTopics(res.data.videos);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [searchQuery, isAdmin, isUser]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Debounce search query updates
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setSearchQuery(searchTerm);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const handleDeleteVideo = () => {
    setIsLoadingDelete(true);
    deleteHelpCenterVideo(showDeleteVideo.data.uuid)
      .then((res) => {
        setShowDeleteVideo({ open: false, data: null });
        setHelpTopics(
          helpTopics.filter((topic) => topic.uuid !== showDeleteVideo.data.uuid)
        );
        SaveApiResMessage({
          title: 'Success',
          message: 'Video deleted successfully',
          mode: 'success',
        });
      })
      .catch((err) => {
        SaveApiResMessage({
          title: 'Error',
          message: err?.message || 'Failed to delete video',
          mode: 'error',
        });
      })
      .finally(() => {
        setIsLoadingDelete(false);
      });
  };

  const resetSearch = () => {
    setSearchTerm('');
    setSearchQuery('');
  };

  return (
    <>
      <PageContainerAB mainPage minWidth={'unset'} title="Help Center">
        <div className="w-full mt-4">
          <div
            className="relative bg-gray-6000 rounded-md overflow-hidden mb-8 md:h-56"
            style={{
              border: '1px solid #3A3A3A',
              background:
                'radial-gradient(206.29% 83.93% at 50% 16.07%, rgba(58, 58, 58, 0.00) 0%, #3A3A3A 100%), url(<path-to-image>) lightgray 0% 0% / 69.9999988079071px 69.9999988079071px repeat, #3A3A3A',
              backgroundBlendMode: 'normal, luminosity, normal',
            }}>
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-gray-6000" />
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-b from-transparent via-transparent to-gray-6000" />

            <div className="relative h-full flex-col-reverse md:flex-row flex items-center justify-between p-4 md:py-0 xl:px-8 gap-4">
              <div className="flex flex-col gap-6 md:w-2/3">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-3.5">
                    <Icon src={Icons.questionMark} className="w-5 h-5" />

                    <h2 className="text-white text-md md:text-xl font-bold m-0">
                      How can we help you?
                    </h2>
                  </div>
                  <p className="m-0 text-white text-sm md:text-lg ">
                    Watch the short 1-minute video below to learn how to use
                    Smart Turn to find and request empty containers for your
                    street turn transactions.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-between h-10 px-2 border-solid border border-border-tertiary rounded bg-transparent w-72">
                    <input
                      type="text"
                      placeholder="Search for help topics..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="flex-1 border-none shadow-none bg-transparent text-white text-sm  placeholder-white placeholder-opacity-70 outline-none"
                    />
                    {searchTerm && (
                      <div
                        onClick={() => resetSearch()}
                        className="cursor-pointer">
                        <Icon src={Icons.whiteClose} className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Video placeholder area */}
              <div className=" h-48 flex items-center justify-end">
                <img
                  src={helpCenterBackground}
                  alt="help center background"
                  className="w-auto h-full object-contain"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col xl:flex-row gap-6">
            <div className="flex-1">
              <div className="mb-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <h2 className="text-base md:text-xl font-bold text-primary-text m-0 mb-1">
                    Browse Help Topics
                  </h2>
                  <p className="text-sm md:text-lg text-secondary-text capitalize m-0">
                    Find answers to your questions and explore helpful
                    resources.
                  </p>
                </div>
                {isAdmin && (
                  <IconButton
                    icon="videoPlay"
                    onClick={() =>
                      setShowUploadVideo({
                        open: true,
                        data: null,
                      })
                    }
                    text="Upload New Video"
                    color="white"
                    style={{
                      backgroundColor: '#131517',
                      height: 44,
                      fontFamily: 'Averta',
                    }}
                  />
                )}
              </div>

              <div className="flex flex-col gap-4">
                {isLoading ? (
                  <div className="flex items-center justify-center mt-4">
                    <LoadingV2 />
                  </div>
                ) : (
                  helpTopics.map((topic, index) => (
                    <VideoCard
                      key={topic.uuid}
                      videoData={topic}
                      index={index}
                      isAdmin={isAdmin}
                      isUser={isUser}
                      onEdit={() =>
                        setShowUploadVideo({
                          open: true,
                          data: topic,
                        })
                      }
                      onDelete={() =>
                        setShowDeleteVideo({
                          open: true,
                          data: topic,
                        })
                      }
                      onViewersList={() =>
                        setShowViewersList({
                          open: true,
                          data: topic,
                        })
                      }
                    />
                  ))
                )}
              </div>
            </div>

            <ContactSupport />
          </div>
        </div>
      </PageContainerAB>

      <UploadVideo
        show={showUploadVideo.open}
        setShow={setShowUploadVideo}
        data={showUploadVideo.data}
        refetchList={fetchList}
      />

      {createPortal(
        <Modal
          show={showDeleteVideo.open}
          children={
            <span
              style={{
                textAlign: 'center',
                width: '100%',
                marginTop: '24px',
              }}>
              Are you sure you want to delete this video?
            </span>
          }
          submitText={'Yes, Delete'}
          icon={'deleteIcon'}
          title={'Delete Video'}
          setShow={() => setShowDeleteVideo({ open: false, data: null })}
          submit={handleDeleteVideo}
          loadingbutton={isLoadingDelete}
        />,
        document.body
      )}
      {showViewersList.open && (
        <ViewersList
          show={showViewersList.open}
          setShow={setShowViewersList}
          videoId={showViewersList.data.uuid}
        />
      )}
    </>
  );
};

export default HelpCenter;
